import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Users, Building2, Truck, FileText, Clock, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    vehicles: 0,
    bookings: 0,
    pending_bookings: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/bookings`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: "Utenti", value: stats.users, icon: Users, color: "text-blue-600", href: "/admin/utenti" },
    { label: "Aziende", value: stats.companies, icon: Building2, color: "text-purple-600", href: "/admin/aziende" },
    { label: "Veicoli", value: stats.vehicles, icon: Truck, color: "text-green-600", href: "/admin/veicoli" },
    { label: "Prenotazioni", value: stats.bookings, icon: FileText, color: "text-orange-600", href: "/admin/prenotazioni" },
  ];

  return (
    <DashboardLayout title="Dashboard Admin">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome */}
        <div className="bg-foreground text-background p-6">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Pannello di Controllo
          </h2>
          <p className="text-muted-foreground">
            Gestisci utenti, aziende, veicoli e prenotazioni della piattaforma.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card 
              key={stat.label} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => stat.href !== "#" && navigate(stat.href)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{stat.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending Alert */}
        {stats.pending_bookings > 0 && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-primary p-3">
                    <Clock className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold">Prenotazioni in Attesa</h3>
                    <p className="text-sm text-muted-foreground">
                      Ci sono {stats.pending_bookings} prenotazioni che richiedono attenzione
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate("/admin/prenotazioni")} className="btn-primary py-2" data-testid="view-pending">
                  Visualizza
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Gestione Utenti</h3>
                  <p className="text-sm text-muted-foreground">
                    Visualizza e modifica utenti
                  </p>
                </div>
                <Button variant="ghost" onClick={() => navigate("/admin/utenti")} data-testid="manage-users">
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-3">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Gestione Aziende</h3>
                  <p className="text-sm text-muted-foreground">
                    Visualizza e modifica aziende
                  </p>
                </div>
                <Button variant="ghost" onClick={() => navigate("/admin/aziende")} data-testid="manage-companies">
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-3">
                  <FileText className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Prenotazioni</h3>
                  <p className="text-sm text-muted-foreground">
                    Gestisci tutte le prenotazioni
                  </p>
                </div>
                <Button variant="ghost" onClick={() => navigate("/admin/prenotazioni")} data-testid="manage-bookings">
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Prenotazioni Recenti
            </CardTitle>
            <Button variant="ghost" onClick={() => navigate("/admin/prenotazioni")}>
              Vedi Tutte
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : recentBookings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nessuna prenotazione
              </div>
            ) : (
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="flex items-center justify-between p-4 bg-muted/50 border border-border"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 p-2">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">#{booking.booking_id.slice(-8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                      <span className="font-mono font-bold">{formatCurrency(booking.total_price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
